import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import thShield from "@/assets/th-shield.png";

import { useI18n } from "@/context/I18nContext";

const DEMO_REQUEST_URL = "https://titanhavengroup.com/contact";

export default function Login() {
  const {
    t
  } = useI18n();

  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in");
      navigate("/today");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    (<div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="hidden w-1/2 flex-col justify-between bg-sidebar p-12 text-sidebar-foreground lg:flex">
        <div className="flex items-center gap-3">
          <img src={thShield} alt={t("Titan Haven Group Product")} className="h-11 w-auto drop-shadow-sm" data-testid="login-th-shield" />
          <div>
            <div className="font-display text-lg font-bold">{t("WarRoom StoreCentral")}</div>
            <div className="text-xs uppercase tracking-widest text-sidebar-muted">{t("Retail Operating Platform")}</div>
            <div className="mt-1 text-[11px] text-sidebar-muted" data-testid="login-th-tagline">{t("A Titan Haven Group Product")}</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="font-display text-4xl font-bold leading-tight lg:text-5xl">{t("Run the store day, not just the dashboard.")}</h1>
          <p className="mt-4 text-sidebar-muted">{t(
            "Connect plan, live conditions, management response, execution, result, and handoff — in one tenant-safe platform. One predictable price. Use what you need."
          )}</p>
        </div>
        <div className="text-xs text-sidebar-muted">{t("© Titan Haven Group LLC · WarRoom Solutions")}</div>
      </div>
      {/* Right form */}
      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-16">
        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2.5">
              <img src={thShield} alt={t("Titan Haven Group Product")} className="h-9 w-auto" data-testid="login-th-shield-mobile" />
              <div>
                <div className="font-display text-lg font-bold leading-tight">{t("WarRoom StoreCentral")}</div>
                <div className="text-[11px] text-muted-foreground">{t("A Titan Haven Group Product")}</div>
              </div>
            </div>
          </div>
          <h2 className="font-display text-2xl font-bold">{t("Sign in")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("Enterprise access. Contact your administrator for an account.")}</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">{t("Work email")}</Label>
              <Input id="email" type="email" required value={email} autoComplete="username"
                onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
                className="mt-1.5" placeholder={t("you@company.com")} />
            </div>
            <div>
              <Label htmlFor="password">{t("Password")}</Label>
              <Input id="password" type="password" required value={password} autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                className="mt-1.5" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full">
              {busy ? t("Signing in…") : t("Sign in")}
            </Button>
          </form>

          <div className="mt-8 rounded-lg border border-border bg-muted/50 p-5" data-testid="request-demo-panel">
            <p className="text-sm text-muted-foreground">{t("Want to see StoreCentral in action? Request a guided demonstration from Titan Haven Group.")}</p>
            <a
              href={DEMO_REQUEST_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="request-demo-cta"
              className="mt-3 inline-flex w-full items-center justify-center rounded-md border border-brand px-4 py-2 text-sm font-semibold text-brand transition-colors hover:bg-brand hover:text-brand-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
            >
              {t("Request a Demo")}
            </a>
            <a href="/demo" data-testid="login-try-sample-demo" className="mt-3 block text-center text-sm font-medium text-brand underline-offset-4 hover:underline">
              {t("Try the sample demo (no login, fictional data)")}
            </a>
          </div>
        </div>
      </div>
    </div>)
  );
}
